import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { 
  createSupplierReturn, 
  SupplierReturnCreate,
  SupplierReturnReason 
} from '../../services/supplierReturnService';
import { fetchItemsAsync } from '../inventory/inventorySlice';

interface CreateSupplierReturnModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface ReturnItem {
  id: string;
  item_id: number | '';
  item_name: string;
  item_sku: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
  searchQuery: string;
  showSuggestions: boolean;
}

const CreateSupplierReturnModal: React.FC<CreateSupplierReturnModalProps> = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { items } = useSelector((state: RootState) => state.inventory);
  
  const [supplierId, setSupplierId] = useState<number>(1);
  const [reason, setReason] = useState<SupplierReturnReason>(SupplierReturnReason.DEFECT);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Initialize dengan 25 baris kosong
  const [returnItems, setReturnItems] = useState<ReturnItem[]>(() => {
    return Array.from({ length: 25 }, (_, i) => ({
      id: `item-${i}`,
      item_id: '',
      item_name: '',
      item_sku: '',
      quantity: 0,
      unit_price: 0,
      subtotal: 0,
      searchQuery: '',
      showSuggestions: false,
    }));
  });

  useEffect(() => {
    if (items.length === 0) {
      dispatch(fetchItemsAsync() as any);
    }
  }, [dispatch, items.length]);

  const getFilteredItems = (searchQuery: string, maxResults: number = 20) => {
    if (!searchQuery || searchQuery.length < 2) return [];
    
    const search = searchQuery.toLowerCase();
    return items
      .filter(item => 
        item.name.toLowerCase().includes(search) ||
        item.sku.toLowerCase().includes(search) ||
        (item.brand && item.brand.toLowerCase().includes(search))
      )
      .slice(0, maxResults);
  };

  const handleSearchChange = (index: number, value: string) => {
    const newItems = [...returnItems];
    newItems[index] = {
      ...newItems[index],
      searchQuery: value,
      showSuggestions: value.length >= 2,
    };
    setReturnItems(newItems);
  };

  const handleItemSelect = (index: number, selectedItem: any) => {
    const newItems = [...returnItems];
    newItems[index] = {
      ...newItems[index],
      item_id: selectedItem.id,
      item_name: selectedItem.name,
      item_sku: selectedItem.sku,
      unit_price: selectedItem.price || 0,
      subtotal: newItems[index].quantity * (selectedItem.price || 0),
      searchQuery: `${selectedItem.name} (${selectedItem.sku})`,
      showSuggestions: false,
    };
    setReturnItems(newItems);
    
    // Auto focus ke quantity input
    setTimeout(() => {
      const qtyInput = document.getElementById(`qty-${index}`);
      if (qtyInput) qtyInput.focus();
    }, 100);
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...returnItems];
    
    if (field === 'quantity') {
      const qty = parseInt(value) || 0;
      newItems[index] = {
        ...newItems[index],
        quantity: qty,
        subtotal: qty * newItems[index].unit_price,
      };
    } else if (field === 'unit_price') {
      const price = parseFloat(value) || 0;
      newItems[index] = {
        ...newItems[index],
        unit_price: price,
        subtotal: newItems[index].quantity * price,
      };
    }
    
    setReturnItems(newItems);
  };

  const clearItem = (index: number) => {
    const newItems = [...returnItems];
    newItems[index] = {
      ...newItems[index],
      item_id: '',
      item_name: '',
      item_sku: '',
      quantity: 0,
      unit_price: 0,
      subtotal: 0,
      searchQuery: '',
      showSuggestions: false,
    };
    setReturnItems(newItems);
  };

  const addMoreRows = () => {
    const newRows = Array.from({ length: 10 }, (_, i) => ({
      id: `item-${returnItems.length + i}`,
      item_id: '',
      item_name: '',
      item_sku: '',
      quantity: 0,
      unit_price: 0,
      subtotal: 0,
      searchQuery: '',
      showSuggestions: false,
    }));
    setReturnItems([...returnItems, ...newRows]);
  };

  const calculateTotal = () => {
    return returnItems.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const getValidItems = () => {
    return returnItems.filter(item => item.item_id && item.quantity > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validItems = getValidItems();
    
    if (validItems.length === 0) {
      alert('Tambahkan minimal 1 item untuk retur');
      return;
    }

    setLoading(true);
    try {
      const returnData: SupplierReturnCreate = {
        supplier_id: supplierId,
        reason,
        notes: notes || undefined,
        items: validItems.map(item => ({
          item_id: item.item_id as number,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      };
      
      await createSupplierReturn(returnData);
      alert(`Retur ke supplier berhasil dibuat!\nTotal Items: ${validItems.length}\nTotal Nilai: Rp ${calculateTotal().toLocaleString()}`);
      onSuccess();
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || error.message || 'Terjadi kesalahan';
      alert(`Error membuat retur:\n${errorMsg}`);
      console.error('Supplier return error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl mx-4 my-8 max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold">Buat Retur Pembelian ke Supplier</h2>
            <p className="text-sm text-gray-500 mt-1">Ketik minimal 2 karakter untuk mencari barang dengan cepat</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6">
            {/* Header Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2">Supplier *</label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded"
                  required
                >
                  <option value={1}>Supplier 1 - PT Maju Jaya</option>
                  <option value={2}>Supplier 2 - CV Berkah</option>
                  <option value={3}>Supplier 3 - Toko Grosir</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Alasan Retur *</label>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value as SupplierReturnReason)}
                  className="w-full px-3 py-2 border rounded"
                  required
                >
                  <option value={SupplierReturnReason.DEFECT}>Rusak/Cacat</option>
                  <option value={SupplierReturnReason.WRONG_ITEM}>Barang Salah</option>
                  <option value={SupplierReturnReason.EXCESS}>Kelebihan Stok</option>
                  <option value={SupplierReturnReason.OTHER}>Lainnya</option>
                </select>
              </div>
            </div>

            {/* Catatan */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Catatan</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border rounded"
                placeholder="Catatan tambahan (opsional)"
              />
            </div>

            {/* Table Items */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">Daftar Barang yang Diretur</h3>
                <div className="text-sm text-gray-600">
                  Item terisi: <span className="font-bold text-blue-600">{getValidItems().length}</span> / {returnItems.length}
                </div>
              </div>
              
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-3 text-center min-w-[50px] border-b-2 border-gray-200">No</th>
                      <th className="px-2 py-3 text-left w-96 border-b-2 border-gray-200">Cari Item (Ketik SKU/Nama) *</th>
                      <th className="px-2 py-3 text-left w-32 border-b-2 border-gray-200">SKU</th>
                      <th className="px-2 py-3 text-right w-24 border-b-2 border-gray-200">Qty *</th>
                      <th className="px-2 py-3 text-right w-32 border-b-2 border-gray-200">Harga Satuan *</th>
                      <th className="px-2 py-3 text-right w-32 border-b-2 border-gray-200">Subtotal</th>
                      <th className="px-2 py-3 text-center w-16 border-b-2 border-gray-200">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returnItems.map((item, index) => (
                      <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 text-center text-gray-700 font-medium min-w-[50px]">{index + 1}</td>
                        <td className="px-2 py-1 relative">
                          <input
                            type="text"
                            value={item.searchQuery}
                            onChange={(e) => handleSearchChange(index, e.target.value)}
                            onFocus={(e) => {
                              if (e.target.value.length >= 2) {
                                const newItems = [...returnItems];
                                newItems[index].showSuggestions = true;
                                setReturnItems(newItems);
                              }
                            }}
                            placeholder="Ketik SKU atau nama barang..."
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                          
                          {item.showSuggestions && item.searchQuery.length >= 2 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                              {getFilteredItems(item.searchQuery).length > 0 ? (
                                getFilteredItems(item.searchQuery).map(suggestion => (
                                  <div
                                    key={suggestion.id}
                                    onClick={() => handleItemSelect(index, suggestion)}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b"
                                  >
                                    <div className="font-medium text-sm">{suggestion.name}</div>
                                    <div className="text-xs text-gray-500">
                                      SKU: {suggestion.sku} | Stock: {suggestion.stock} | Harga: Rp {suggestion.price?.toLocaleString()}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-2 text-gray-500 text-sm">
                                  Tidak ada hasil. Ketik SKU atau nama yang lebih spesifik.
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1 text-gray-600 text-sm">
                          {item.item_sku || '-'}
                        </td>
                        <td className="px-2 py-1">
                          <input
                            id={`qty-${index}`}
                            type="number"
                            min="0"
                            value={item.quantity || ''}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const nextIndex = index + 1;
                                if (nextIndex < returnItems.length) {
                                  const nextInput = document.querySelector(`tr:nth-child(${nextIndex + 1}) input[type="text"]`) as HTMLInputElement;
                                  if (nextInput) nextInput.focus();
                                }
                              }
                            }}
                            className="w-full px-2 py-1 border rounded text-right"
                            disabled={!item.item_id}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            value={item.unit_price || ''}
                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                            className="w-full px-2 py-1 border rounded text-right text-sm"
                            disabled={!item.item_id}
                          />
                        </td>
                        <td className="px-2 py-1 text-right font-medium text-sm">
                          {item.subtotal > 0 ? `Rp ${item.subtotal.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {item.item_id && (
                            <button
                              type="button"
                              onClick={() => clearItem(index)}
                              className="text-red-500 hover:text-red-700 text-xs"
                              title="Hapus"
                            >
                              ✕
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 font-bold sticky bottom-0">
                    <tr>
                      <td colSpan={5} className="px-2 py-2 text-right">TOTAL:</td>
                      <td className="px-2 py-2 text-right text-lg text-blue-600">
                        Rp {calculateTotal().toLocaleString()}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button
                type="button"
                onClick={addMoreRows}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                + Tambah 10 Baris Lagi
              </button>
            </div>
          </div>

          {/* Actions - Sticky Footer */}
          <div className="p-6 border-t bg-gray-50 sticky bottom-0">
            <div className="flex gap-3 justify-between items-center">
              <div className="text-sm text-gray-600">
                Total: <span className="font-bold text-lg text-blue-600">Rp {calculateTotal().toLocaleString()}</span>
                {' | '}
                Items: <span className="font-bold">{getValidItems().length}</span>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={loading || getValidItems().length === 0}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loading ? 'Memproses...' : 'Buat Retur Pembelian'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSupplierReturnModal;
